import { useEffect, useRef } from "react";

import { Badge } from "@/components/ui/badge";

import { LinkBtn } from "./Bubble.jsx";
import { ClosedMark } from "./ClosedMark.jsx";
import { NewMark } from "./NewMark.jsx";
import { Stars } from "./Stars.jsx";
import { WantMark } from "./WantMark.jsx";
import { authorOf, ratingOf, reviewOf, statusOf } from "../lib/reviews.js";
import { cn, fmtDist, fmtWon } from "../lib/util.js";

/* 높이를 여기서 못박지 않는다. 남는 자리는 App 의 grid 가 나눠주고(auto 1fr), 이쪽은
   그 안에서 스크롤만 한다 — 여기에도 상한을 두면 두 곳이 같은 높이를 정하게 된다. */
const LIST = "list flex flex-1 flex-col gap-2 overflow-y-auto bg-ground p-2.5";

/* 누르는 게 아니라 읽는 것이다 — 테두리를 없애 버튼처럼 보이지 않게 한다.
   대신 메타줄(muted-foreground)보다 한 단계 진한 색으로 정보로서 눈에 걸리게 둔다. */
function Info({ children }) {
  return (
    <Badge variant="ghost"
           className="badge px-0 text-[11.5px] font-semibold whitespace-nowrap text-ink-2">
      {children}
    </Badge>
  );
}

function StoreCard({ store, reviews, status, selected, onSelect, onOpenReviews, onToggleWant }) {
  const r = reviewOf(reviews, store.id);
  const { score, count } = ratingOf(reviews, store.id);

  const bits = [store.category || "기타"];
  // '찜'을 글자로 적지 않는다 — 이름 옆 하트가 같은 말을 하고, 찜도
  // 아직 안 가본 곳이라 여기서는 '미방문'이 맞다. 휴무도 여기 안 적는다 — 이름 옆
  // 배지(ClosedMark)가 같은 말을 한다.
  if (!r.visits.length) bits.push("미방문");

  /* 가게에 가기 전에 알아야 하는 것만 붙인다.
     식권대장 별점은 안 쓴다 — 별점은 우리 사원들이 남긴 기록으로만 매긴다.
     note(캡처 메모)와 카카오 등록명은 매칭을 검수하던 개발 단계의 흔적이라 내렸다.
     둘 다 데이터에는 남아 있다. 등록명은 검색·링크가 계속 쓴다(filter.js, links.py). */
  const extras = [];
  if (store.hours_raw) extras.push(<Info key="hours">{store.hours_raw}</Info>);
  if (store.delivery) extras.push(<Info key="delivery">배달</Info>);

  /* 카드에 펴는 리뷰는 가장 최근 한 건. 시트의 리뷰 목록과 같은 모양으로,
     메뉴·가격이 한 줄이고 한줄평이 그 아래 제 줄에 선다 — 값과 문장을 한 줄로 이으면
     접히는 자리에 구분점이 매달린다(Sheet.jsx 의 같은 자리 참고).
     두 줄을 서로 다른 방문에서 긁어오지는 않는다. 남이 먹은 메뉴 밑에 내 한줄평이
     붙으면 한 사람이 남긴 리뷰로 읽힌다. 그래서 별점만 남긴 방문은 펼 것이 없으니
     건너뛰고, 적어도 한 칸은 채운 가장 최근 방문을 고른다.
     팀 기록이 된 뒤로는 남의 말일 수 있어서 이름을 붙인다 — 안 붙이면 카드에 적힌
     한 줄이 전부 내 말처럼 읽힌다. */
  const last = r.visits.find((v) => v.menu || v.price || v.memo);
  const facts = last
    ? [last.menu, last.price ? fmtWon(last.price) : null].filter(Boolean).join(" / ")
    : "";

  return (
    <button
      type="button"
      className={cn(
        /* 왼쪽 여백만 넉넉히 남겨 완전히 왼쪽에 붙지 않게 한다. 상태 동그라미를
           없앤 뒤로 2단 그리드가 필요 없어졌다. */
        "card block w-full rounded-[10px] border border-border bg-card p-[12px_14px_12px_26px]",
        "text-left shadow-card hover:border-input",
        status === "unvisited" && "opacity-60 hover:opacity-100",
        selected && "sel border-primary opacity-100 shadow-[0_0_0_2px_var(--color-primary-soft)]",
      )}
      data-state={status}
      data-id={store.id}
      onClick={(e) => {
        if (e.target.closest("a")) { e.stopPropagation(); return; }
        onSelect(store.id, true);
      }}
    >
      {/* 리스트에는 상태 동그라미를 그리지 않는다. 상태는 메타줄과 방문 횟수로 읽히고,
          동그라미는 지도 마커의 몫이다. data-state 는 카드 흐림 처리에 계속 쓰인다.
          min-w-0 이 없으면 긴 상호명·배지가 그리드 칸을 넘겨 가로 스크롤을 만든다.
          줄바꿈은 말풍선과 같은 규칙이다 — 어절에서 끊고(break-keep), 띄어쓰기 없는
          긴 이름만 아무 데서나 끊는다(wrap-anywhere). Bubble.jsx 참고. */}
      <div className="min-w-0 break-keep wrap-anywhere">
        {/* 접히는 것(이름)과 안 접히는 것(거리)을 칸으로 가른다 — 넷을 한 줄에 늘어놓고
            flex-wrap 에 맡기면 이름이 두 줄이 되는 순간 ml-auto 로 밀어둔 거리가 제 줄로
            떨어져, 빈 줄 오른쪽 끝에 '59m' 혼자 남는다. 거리는 폭이 뻔한 칸이라 접히는
            이름 옆에 세워두면 그만이다. 하트와 ×N 은 이름 끝에 붙어야 뜻이 사는 표식이라
            칸으로 떼지 않고 이름과 같은 글줄에 인라인으로 흘린다(Bubble.jsx 도 같은 규칙). */}
        <div className="card-name flex items-baseline gap-[7px]">
          <div className="min-w-0 flex-1">
            <strong className="text-[15px] font-bold tracking-[-.01em]">{store.name}</strong>
            <NewMark store={store} className="ml-[7px]" />
            <ClosedMark store={store} className="ml-[7px]" />
            <WantMark on={r.want_to_go} onToggle={() => onToggleWant(store.id)}
                      className="ml-[7px] align-middle" />
            {r.visits.length > 1 && (
              <span className="count ml-[7px] text-[11.5px] font-bold text-primary">
                ×{r.visits.length}
              </span>
            )}
          </div>
          {store._dist != null && (
            <span className="dist flex-none text-[11.5px] text-muted-foreground">
              {fmtDist(store._dist)}
            </span>
          )}
        </div>

        <div className="card-meta text-[12.5px] text-muted-foreground">{bits.join(" · ")}</div>

        {extras.length > 0 && (
          <div className="mt-[5px] flex flex-wrap items-baseline gap-[7px]">{extras}</div>
        )}

        {last && (
          /* 두 줄을 하나의 세로줄이 묶는다 — 한 사람이 남긴 한 건이라는 표시다.
             둘 다 한 줄로 자른다. 카드는 훑는 곳이라 여기서 세 줄, 네 줄로 자라면
             밑의 별점 줄이 카드마다 다른 높이에 서서 목록을 훑기 어려워진다. */
          <div className="card-review mt-[3px] border-l-2 border-input pl-2 text-[12.5px] text-ink-2">
            {facts && (
              <div className="card-menu overflow-hidden text-ellipsis whitespace-nowrap">{facts}</div>
            )}
            {last.memo && (
              <div className="card-memo overflow-hidden text-ellipsis whitespace-nowrap">
                <span className={cn("memo-by", last.mine ? "text-primary" : "text-muted-foreground")}>
                  {authorOf(last)}
                </span>
                {` ${last.memo}`}
              </div>
            )}
          </div>
        )}

        <div className="card-links mt-2 flex flex-wrap items-center gap-1.5">
          <LinkBtn href={store.links?.naver} brand="naver" />
          <LinkBtn href={store.links?.kakao} brand="kakao" />

          {/* 우리 기록으로만 매긴 별점 · 리뷰 수. 아직 안 가본 집은 0.0점 · 0건으로,
              평가가 없다는 사실을 그대로 보여준다. */}
          <span className="rating ml-0.5 flex items-baseline gap-1.5 text-[11.5px]">
            <b className="font-bold text-foreground">{score.toFixed(1)}</b>
            <Stars score={score} className="text-[11px]" />
            {/* 카드 전체 클릭은 지도 말풍선을 띄운다. 여기는 그것과 다른 목적지(기록 시트)라
                이벤트를 카드로 흘려보내지 않는다. 카드 안의 두 번째 조작점이므로
                키보드로도 닿아야 한다. */}
            <span
              role="button"
              tabIndex={0}
              className="reviews-open cursor-pointer whitespace-nowrap text-muted-foreground
                         underline-offset-2 hover:text-primary hover:underline"
              onClick={(e) => { e.stopPropagation(); onOpenReviews(store.id); }}
              onKeyDown={(e) => {
                if (e.key !== "Enter" && e.key !== " ") return;
                e.preventDefault();
                e.stopPropagation();
                onOpenReviews(store.id);
              }}
            >
              리뷰 {count}건
            </span>
          </span>
        </div>
      </div>
    </button>
  );
}

export function StoreList({ rows, reviews, selectedId, kakaoInfo, query, className, matched,
                            onSelect, onOpenReviews, onToggleWant }) {
  const listRef = useRef(null);

  useEffect(() => {
    if (!selectedId) return;
    listRef.current?.querySelector(`.card[data-id="${selectedId}"]`)
      ?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [selectedId, rows]);

  if (!rows.length) {
    /* 빈 목록에는 세 가지 이유가 있고, 셋을 구별해줘야 다음에 뭘 할지 알 수 있다.
       1. 조건에 맞는 곳은 있는데 지도가 딴 데를 보고 있다 → 지도를 옮기면 된다.
          이건 목록이 지도를 따라가게 된 뒤로 가장 흔한 경우인데, "표시할 식당이 없습니다"
          한 줄로는 필터를 잘못 건 줄 알고 엉뚱한 데를 고치게 된다.
       2. 카카오는 찾았는데 제휴점이 아니다 → 제휴가 안 된 곳들이라는 뜻. 유용한 정보다.
       3. 그냥 아무것도 없다. */
    const msg = matched > 0
      ? `이 화면에는 없습니다. 조건에 맞는 ${matched}곳은 지도 밖에 있습니다.`
      : query.trim() && kakaoInfo?.total
        ? `카카오는 ${kakaoInfo.total}곳을 찾았지만 제휴점은 없습니다.`
        : "표시할 식당이 없습니다.";
    return (
      <div className={cn(LIST, className)} id="list" ref={listRef}>
        <p className="empty px-5 py-10 text-center text-sm text-muted-foreground">{msg}</p>
      </div>
    );
  }

  return (
    <div className={cn(LIST, className)} id="list" ref={listRef}>
      {rows.map((s) => (
        <StoreCard
          key={s.id}
          store={s}
          reviews={reviews}
          status={statusOf(reviews, s)}
          selected={s.id === selectedId}
          onSelect={onSelect}
          onOpenReviews={onOpenReviews}
          onToggleWant={onToggleWant}
        />
      ))}
    </div>
  );
}
